import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { toast } from "../lib/toast";

const PROVIDER = "kroger";
const RADIUS_OPTIONS = [5, 10, 25, 50, 100];
const KROGER_CART_URL = "https://www.kroger.com/shopping/cart";

function formatMatchLabel(match) {
  if (!match?.matched_name) return "No product selected";
  const parts = [match.matched_name];
  if (match.matched_size) parts.push(match.matched_size);
  if (match.matched_brand) parts.push(match.matched_brand);
  return parts.join(" · ");
}

function ResultPill({ children, tone = "gray" }) {
  const tones = {
    green: "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-900/40",
    yellow: "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-900/40",
    gray: "bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-700/60 dark:text-gray-300 dark:border-gray-600",
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

export default function RetailerReviewPanel({ items, compact = false, source = "shopping_list", onCartSendSuccess = null }) {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [zipCode, setZipCode] = useState("");
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [stores, setStores] = useState([]);
  const [storeSearchLoading, setStoreSearchLoading] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [storeSaving, setStoreSaving] = useState(false);
  const [showStoreEditor, setShowStoreEditor] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [openItemId, setOpenItemId] = useState(null);
  const [searchOverride, setSearchOverride] = useState("");
  const [candidateLoadingId, setCandidateLoadingId] = useState(null);
  const [candidatesByItemId, setCandidatesByItemId] = useState({});
  const [updatingMatchId, setUpdatingMatchId] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [cartSending, setCartSending] = useState(false);

  const totalReviewCount = useMemo(() => {
    if (!preview) return 0;
    return (preview.needs_review?.length || 0) + (preview.no_match?.length || 0);
  }, [preview]);

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await axiosInstance.get("/api/integrations/retailers/status/", {
        params: { provider: PROVIDER },
      });
      setStatus(res.data);
      setZipCode(res.data.store_preference?.zip_code || "");
      setSelectedStoreId(res.data.store_preference?.location_id || "");
    } catch (err) {
      console.error("Failed to fetch retailer status:", err);
      toast.error("Could not load Kroger setup.");
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const searchStores = useCallback(async ({ silent = false } = {}) => {
    if (!zipCode.trim()) {
      if (!silent) toast.error("Enter a ZIP code first.");
      return [];
    }

    setStoreSearchLoading(true);
    try {
      const res = await axiosInstance.get("/api/integrations/retailers/stores/", {
        params: {
          provider: PROVIDER,
          zip: zipCode.trim(),
          radius_miles: radiusMiles,
        },
      });
      const nextStores = res.data.stores || [];
      setStores(nextStores);
      if (!nextStores.length && !silent) {
        toast.error(`No Kroger stores found within ${radiusMiles} miles.`);
      }
      return nextStores;
    } catch (err) {
      console.error("Failed to search stores:", err);
      if (!silent) toast.error("Could not search Kroger stores.");
      return [];
    } finally {
      setStoreSearchLoading(false);
    }
  }, [radiusMiles, zipCode]);

  const saveStore = useCallback(async (store, { silent = false } = {}) => {
    setStoreSaving(true);
    try {
      const modality = store.modalities?.[0] || "PICKUP";
      await axiosInstance.post("/api/integrations/retailers/store/select/", {
        provider: PROVIDER,
        zip_code: zipCode.trim(),
        location_id: store.location_id,
        location_name: store.store_name,
        address: store.address,
        modality,
      });
      setPreview(null);
      setSelectedStoreId(store.location_id);
      setShowStoreEditor(false);
      await fetchStatus();
      if (!silent) toast.success("Kroger store saved.");
      return true;
    } catch (err) {
      console.error("Failed to save store:", err);
      if (!silent) toast.error("Could not save selected store.");
      return false;
    } finally {
      setStoreSaving(false);
    }
  }, [fetchStatus, zipCode]);

  const handleSearchStores = async () => {
    await searchStores();
  };

  const autoSelectNearestStore = useCallback(async () => {
    const nextStores = await searchStores({ silent: true });
    if (!nextStores.length) return;
    const firstStore = nextStores[0];
    if (!firstStore) return;
    await saveStore(firstStore, { silent: true });
  }, [saveStore, searchStores]);

  useEffect(() => {
    if (!status || statusLoading) return;
    if (status.store_preference?.selected) return;
    if (!zipCode.trim()) return;

    autoSelectNearestStore();
  }, [autoSelectNearestStore, status, statusLoading, zipCode]);

  const handleSaveStore = async () => {
    const store = stores.find((entry) => entry.location_id === selectedStoreId);
    if (!store) {
      toast.error("Choose a store first.");
      return;
    }

    await saveStore(store);
  };

  const handleGeneratePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await axiosInstance.post("/api/integrations/retailers/match-preview/", {
        provider: PROVIDER,
        source,
      });
      setPreview(res.data);
      setCandidatesByItemId({});
      setOpenItemId(null);
      toast.success("Kroger match preview generated.");
    } catch (err) {
      console.error("Failed to generate match preview:", err);
      toast.error("Could not generate Kroger match preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleLoadCandidates = async (match) => {
    const nextOpen = openItemId === match.shopping_list_item_id ? null : match.shopping_list_item_id;
    setOpenItemId(nextOpen);
    setSearchOverride(match.shopping_list_item?.name || "");

    if (nextOpen === null || candidatesByItemId[match.shopping_list_item_id]) {
      return;
    }

    setCandidateLoadingId(match.shopping_list_item_id);
    try {
      const res = await axiosInstance.get("/api/integrations/retailers/products/search/", {
        params: {
          provider: PROVIDER,
          shopping_list_item_id: match.shopping_list_item_id,
          limit: 8,
        },
      });
      setCandidatesByItemId((prev) => ({
        ...prev,
        [match.shopping_list_item_id]: res.data.results || [],
      }));
    } catch (err) {
      console.error("Failed to load product candidates:", err);
      toast.error("Could not load alternative products.");
    } finally {
      setCandidateLoadingId(null);
    }
  };

  const handleRefinedSearch = async (match) => {
    setCandidateLoadingId(match.shopping_list_item_id);
    try {
      const res = await axiosInstance.get("/api/integrations/retailers/products/search/", {
        params: {
          provider: PROVIDER,
          shopping_list_item_id: match.shopping_list_item_id,
          q: searchOverride.trim(),
          limit: 8,
        },
      });
      setCandidatesByItemId((prev) => ({
        ...prev,
        [match.shopping_list_item_id]: res.data.results || [],
      }));
    } catch (err) {
      console.error("Failed to refine product search:", err);
      toast.error("Could not refine product search.");
    } finally {
      setCandidateLoadingId(null);
    }
  };

  const handleUseCandidate = async (match, candidate) => {
    setUpdatingMatchId(match.id);
    try {
      const res = await axiosInstance.post("/api/integrations/retailers/match/update/", {
        match_id: match.id,
        provider: PROVIDER,
        matched_upc: candidate.upc,
        matched_name: candidate.description,
        matched_brand: candidate.brand || "",
        matched_size: candidate.size || "",
        matched_price: candidate.price,
        product_snapshot: candidate.product_snapshot || {},
      });

      setPreview((prev) => {
        if (!prev) return prev;
        const updated = res.data;
        return {
          ...prev,
          matched: [updated, ...(prev.matched || []).filter((entry) => entry.id !== match.id)],
          needs_review: (prev.needs_review || []).filter((entry) => entry.id !== match.id),
          no_match: (prev.no_match || []).filter((entry) => entry.id !== match.id),
        };
      });

      toast.success("Product selection saved.");
    } catch (err) {
      console.error("Failed to update match:", err);
      toast.error("Could not save selected product.");
    } finally {
      setUpdatingMatchId(null);
    }
  };

  const handleConnectRetailer = async () => {
    setConnectLoading(true);
    try {
      const res = await axiosInstance.get("/api/integrations/retailers/connect-url/", {
        params: { provider: PROVIDER },
      });
      const authorizeUrl = res.data?.authorize_url;
      if (!authorizeUrl) {
        throw new Error("Missing Kroger authorize URL.");
      }
      window.location.href = authorizeUrl;
    } catch (err) {
      console.error("Failed to start Kroger connect flow:", err);
      toast.error("Could not start Kroger connection.");
      setConnectLoading(false);
    }
  };

  const handleSendToRetailerCart = async () => {
    const cartWindow = window.open("", "_blank");
    setCartSending(true);
    try {
      const res = await axiosInstance.post("/api/integrations/retailers/cart/send/", {
        provider: PROVIDER,
        source,
      });

      if (cartWindow) {
        cartWindow.location.href = KROGER_CART_URL;
        cartWindow.focus();
      } else {
        window.open(KROGER_CART_URL, "_blank");
      }

      if (onCartSendSuccess) {
        await onCartSendSuccess(res.data);
      } else {
        toast.success(`Added ${res.data.sent} items to Kroger cart. Finish checkout in Kroger.`);
      }
    } catch (err) {
      if (cartWindow) {
        cartWindow.close();
      }
      console.error("Failed to send items to Kroger cart:", err);
      toast.error("Could not send items to Kroger cart.");
    } finally {
      setCartSending(false);
    }
  };

  const activeStoreName = status?.store_preference?.location_name || "";
  const reviewList = [...(preview?.needs_review || []), ...(preview?.no_match || [])];
  const canSendToCart = Boolean(preview) && reviewList.length === 0 && (preview?.matched?.length || 0) > 0;

  return (
    <div className={compact ? "space-y-5" : "bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/50 p-5 mb-6"}>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-800 dark:text-white">Kroger Cart Review</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Match your shopping list to Kroger products, then review uncertain items before cart handoff.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ResultPill tone="gray">{items.length} shopping items</ResultPill>
          <ResultPill tone={status?.store_preference?.selected ? "green" : "yellow"}>
            {status?.store_preference?.selected ? "Store saved" : "Store needed"}
          </ResultPill>
          {preview && (
            <ResultPill tone={totalReviewCount > 0 ? "yellow" : "green"}>
              {totalReviewCount > 0 ? `${totalReviewCount} need review` : "Preview ready"}
            </ResultPill>
          )}
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1.1fr,0.9fr]">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-white">1. Store</h3>

          {!zipCode.trim() ? (
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="Enter ZIP code"
                className="flex-1 bg-gray-50 dark:bg-gray-700 border-0 px-4 py-2.5 rounded-lg text-sm dark:text-white dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-600 outline-none"
              />
              <button
                onClick={handleSearchStores}
                disabled={storeSearchLoading || statusLoading}
                className="bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition"
              >
                {storeSearchLoading ? "Searching..." : "Find Store"}
              </button>
            </div>
          ) : (
            <>
              <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 px-4 py-3">
                <p className="text-sm text-gray-700 dark:text-gray-200">
                  {activeStoreName ? (
                    <>Using <span className="font-semibold">{activeStoreName}</span></>
                  ) : (
                    <>Finding your nearest Kroger store...</>
                  )}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {zipCode} · within {radiusMiles} miles
                </p>
              </div>

              <button
                onClick={() => setShowStoreEditor((prev) => !prev)}
                className="mt-3 text-sm text-blue-500 hover:text-blue-600 transition"
              >
                {showStoreEditor ? "Hide store options" : "Change store"}
              </button>

              {showStoreEditor && (
                <div className="mt-4 flex flex-col gap-3">
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                      type="text"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      placeholder="ZIP code"
                      className="flex-1 bg-gray-50 dark:bg-gray-700 border-0 px-4 py-2.5 rounded-lg text-sm dark:text-white dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-600 outline-none"
                    />
                    <select
                      value={radiusMiles}
                      onChange={(e) => setRadiusMiles(Number(e.target.value))}
                      className="bg-gray-50 dark:bg-gray-700 border-0 px-3 py-2.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-600 outline-none"
                    >
                      {RADIUS_OPTIONS.map((value) => (
                        <option key={value} value={value}>Within {value} miles</option>
                      ))}
                    </select>
                    <button
                      onClick={handleSearchStores}
                      disabled={storeSearchLoading || statusLoading}
                      className="bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition"
                    >
                      {storeSearchLoading ? "Searching..." : "Search Again"}
                    </button>
                  </div>

                  {stores.length > 0 && (
                    <>
                      <select
                        value={selectedStoreId}
                        onChange={(e) => setSelectedStoreId(e.target.value)}
                        className="bg-gray-50 dark:bg-gray-700 border-0 px-3 py-2.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-600 outline-none"
                      >
                        <option value="">Choose a store</option>
                        {stores.map((store) => (
                          <option key={store.location_id} value={store.location_id}>
                            {store.store_name} - {store.address}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleSaveStore}
                        disabled={!selectedStoreId || storeSaving}
                        className="self-start bg-gray-900 hover:bg-black disabled:opacity-40 text-white font-medium px-4 py-2 rounded-lg text-sm transition dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
                      >
                        {storeSaving ? "Saving..." : "Use This Store"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-white">2. Build your Kroger draft</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Generate a first-pass product match for your current {source === "cart" ? "checkout list" : "shopping list"}, then review anything uncertain.
          </p>
          <button
            onClick={handleGeneratePreview}
            disabled={!status?.store_preference?.selected || previewLoading}
            className="mt-4 bg-green-500 hover:bg-green-600 disabled:opacity-40 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition"
          >
            {previewLoading ? "Generating..." : "Generate Match Preview"}
          </button>

          {preview && (
            <div className="mt-4 flex flex-wrap gap-2">
              <ResultPill tone="green">{preview.matched?.length || 0} matched</ResultPill>
              <ResultPill tone="yellow">{preview.needs_review?.length || 0} needs review</ResultPill>
              <ResultPill tone="gray">{preview.no_match?.length || 0} no match</ResultPill>
            </div>
          )}
        </div>
      </div>

      {preview && (
        <div className="mt-5 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">3. Review uncertain items</h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {preview.location_name || activeStoreName}
            </span>
          </div>

          {reviewList.length === 0 ? (
            <p className="mt-3 text-sm text-green-600 dark:text-green-400">
              Everything in this preview was matched cleanly. You are in good shape for cart handoff next.
            </p>
          ) : (
            <div className="mt-4 space-y-3">
              {reviewList.map((match) => {
                const isOpen = openItemId === match.shopping_list_item_id;
                const candidates = candidatesByItemId[match.shopping_list_item_id] || [];
                const loadingCandidates = candidateLoadingId === match.shopping_list_item_id;

                return (
                  <div
                    key={match.id}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-4"
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-800 dark:text-white">
                            {match.shopping_list_item?.quantity ? Number(match.shopping_list_item.quantity) : ""} {match.shopping_list_item?.unit || ""} {match.shopping_list_item?.name}
                          </p>
                          <ResultPill tone="yellow">
                            {match.status === "no_match" ? "No match" : "Needs review"}
                          </ResultPill>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Current suggestion: {formatMatchLabel(match)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleLoadCandidates(match)}
                        className="self-start bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 text-sm text-gray-700 dark:text-gray-200 px-3 py-2 rounded-lg transition"
                      >
                        {isOpen ? "Hide Alternatives" : "Find Alternatives"}
                      </button>
                    </div>

                    {isOpen && (
                      <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <input
                            type="text"
                            value={searchOverride}
                            onChange={(e) => setSearchOverride(e.target.value)}
                            placeholder="Refine search"
                            className="flex-1 bg-gray-50 dark:bg-gray-700 border-0 px-3 py-2 rounded-lg text-sm dark:text-white dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-600 outline-none"
                          />
                          <button
                            onClick={() => handleRefinedSearch(match)}
                            disabled={loadingCandidates}
                            className="bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white font-medium px-4 py-2 rounded-lg text-sm transition"
                          >
                            {loadingCandidates ? "Searching..." : "Search"}
                          </button>
                        </div>

                        <div className="mt-3 space-y-2">
                          {loadingCandidates ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">Loading product options...</p>
                          ) : candidates.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No alternatives loaded yet.</p>
                          ) : (
                            candidates.map((candidate) => (
                              <div
                                key={`${match.id}-${candidate.upc}`}
                                className="flex flex-col gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div>
                                  <p className="text-sm font-medium text-gray-800 dark:text-white">{candidate.description}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {[candidate.brand, candidate.size, candidate.price ? `$${candidate.price}` : null]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </p>
                                </div>
                                <button
                                  onClick={() => handleUseCandidate(match, candidate)}
                                  disabled={updatingMatchId === match.id}
                                  className="self-start bg-green-500 hover:bg-green-600 disabled:opacity-40 text-white font-medium px-3 py-2 rounded-lg text-sm transition"
                                >
                                  {updatingMatchId === match.id ? "Saving..." : "Use This Product"}
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {preview && (
        <div className="mt-5 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white">4. Send to Kroger</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Connect your Kroger account, then send this reviewed list to your Kroger cart.
              </p>
              {!canSendToCart && (
                <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                  Resolve any remaining review items before sending the cart.
                </p>
              )}
            </div>
            {!status?.connection?.connected ? (
              <button
                onClick={handleConnectRetailer}
                disabled={connectLoading}
                className="bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition"
              >
                {connectLoading ? "Connecting..." : "Connect Kroger"}
              </button>
            ) : (
              <button
                onClick={handleSendToRetailerCart}
                disabled={cartSending || !canSendToCart}
                className="bg-green-500 hover:bg-green-600 disabled:opacity-40 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition"
              >
                {cartSending ? "Sending..." : "Send to Kroger Cart"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
